import { BookFailReason, BookStatusCode } from "./../../../types/index";
import {
  BookResultInfo,
  IBookResultInfo,
  IBookResultLineInfo,
} from "@/domain/entities/book";
import { ParseError } from "@/repositories/shared/errors/parseError";
import useGetLineTypeName from "@/use/useGetLineTypeName";
import dayjs from "dayjs";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  HttpBookResultInfo,
  HttpBookDetailInfo,
  HttpLine,
  HttpStop,
  IBookResultInfoParser,
} from "../bookRepository.types";

function getStopInfo(stop: HttpStop) {
  return {
    id: stop.uid,
    name: stop.name,
    arrivalTime: stop.arrivalTime,
    departureTime: stop.departureTime,
  };
}

function getLineInfo(line: HttpLine): IBookResultLineInfo {
  return {
    lineNum: line.lineRouteNo,
    routeName: line.lineRouteName,
    lineTurnUid: line.lineTurnUid,
    turnName: line.lineTurnName,
    lineTypeCode: line.lineTypeCode,
    lineTypeName: useGetLineTypeName(line.lineTypeCode),
    getOnStop: getStopInfo(line.onStation),
    getOffStop: getStopInfo(line.offStation),
    seatAssignYn: line.seatAssignYn === "Y",
    statusCode: line.statusCode,
  };
}

function getBookFailReaseon(detailInfo: HttpBookDetailInfo) {
  if (
    detailInfo.statusCode === BookStatusCode.END ||
    detailInfo.statusCode === BookStatusCode.CANCEL
  ) {
    return "";
  }

  switch (detailInfo.note) {
    case BookFailReason.DUPLICATED:
      return "이미 선택된 좌석";
    case BookFailReason.FULL:
      return "만석";
    default:
      return "오류 발생";
  }
}

function getBookDetails(details: HttpBookDetailInfo[]) {
  return details.map((detailInfo) => {
    return {
      boardDate: dayjs(detailInfo.expectedOnDtm).format("YYYY-MM-DD"),
      expectedOnDateTime: detailInfo.expectedOnDtm,
      expectedOffDateTime: detailInfo.expectedOffDtm,
      bookAmount: detailInfo.bookAmount,
      bookSuccess: detailInfo.statusCode === BookStatusCode.END,
      bookFailedReason: getBookFailReaseon(detailInfo),
      note: detailInfo.note,
    };
  });
}

export type IBookResultDetailInfo = {
  boardDate: string;
  expectedOnDateTime: string;
  expectedOffDateTime: string;
  bookAmount: number;
  bookSuccess: boolean;
  bookFailedReason: string;
  note: string;
  boardingDate: string;
  seatNo?: string;
  statusCode: string;
  allocUid?: string;
};

function parseBoardingDate(
  dates: HttpBookDetailInfo[]
): IBookResultDetailInfo[] {
  return dates.map((date) => {
    const bookDetailInfo: IBookResultDetailInfo = {
      boardDate: dayjs(date.expectedOnDtm).format("YYYY-MM-DD"),
      expectedOnDateTime: date.expectedOnDtm,
      expectedOffDateTime: date.expectedOffDtm,
      bookAmount: date.bookAmount,
      bookSuccess: date.statusCode === BookStatusCode.END,
      bookFailedReason: getBookFailReaseon(date),
      note: date.note,
      boardingDate: dayjs(date.expectedOnDtm).format("YYYY-MM-DD HH:mm"),
      seatNo: date.seatNo ?? date.note,
      statusCode: date.statusCode,
      allocUid: date.allocUid,
    };
    return bookDetailInfo;
  });
}

@injectable()
export class BookResultInfoParser implements IBookResultInfoParser {
  constructor() {}

  toDomain(data: HttpBookResultInfo): Result<IBookResultInfo, ParseError> {
    try {
      const bookResultInfoData: IBookResultInfo = {
        id: data.bookUid,
        bookAmount: data.bookAmount,
        bookIntervalCode: data.bookIntervalCode,
        bookDateTime: dayjs(data.bookTs).format("YYYY-MM-DD HH:mm:ss"),
        bookCancelEndDateTime: data.details?.[data.details.length - 1]
          .cancelEndDtm
          ? dayjs(data.details?.[data.details.length - 1].cancelEndDtm).format(
              "YYYY-MM-DD HH:mm"
            )
          : "",
        lineInfo: getLineInfo(data.line),
        boardingDates: data.details
          ? parseBoardingDate(data.details)
          : undefined,
      };
      const bookResultInfo = new BookResultInfo(bookResultInfoData);
      return ok(bookResultInfo);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
